import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	RevenueSpendByTeamListTable,
	ToolPanel,
	UiPagination,
	ESortOrder,
	RevenueSpendByTeamRevenueDetailsModal,
	RevenueSpendByTeamSpendDetailsModal,
	RevenueSpendByTeamOfferSpendDetailsModal,
	IconView,
	IconViewOff
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import {
	useRevenueSpendByTeamList,
	useRevenueSpendByTeamFiltersPage,
	useRevenueSpendByTeamSortPage
} from '../hooks'
import * as lib from '../lib'
import * as state from '../states'
import {
	IFetchItemsSetup,
	ERoleAccess,
	TRevenueSpendByTeam,
	TRevenueSpendByTeamRevenueCpa,
	TRevenueSpendByTeamRevenueSpend,
	TRevenueSpendByTeamRevenue,
	TRevenueSpendByTeamSpend,
	TRevenueSpendByTeamOfferSpend,
	EOfferType
} from '../types'
import { TAKE, MENU, MENU_CATEGORY } from '../constants'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'


export const RevenueSpendByTeamPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0
	const team = lib.accountTeamSearchParams(searchParams.get('team') ?? '')

	const teamItems = useAtomValue(state.accountTeamsAtom)
	const isRevenueSpendByTeamPageIndex = !team
	const pageTitle = `${t(MENU_CATEGORY.revenue.title)} ${t(MENU.revenueSpendReportByTeam.title)}`
	const pageTitleFormat = `${pageTitle}${!isRevenueSpendByTeamPageIndex ? ': ' + team : ''}`

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueSpendByTeamFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useRevenueSpendByTeamFiltersPage(team)

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useRevenueSpendByTeamSortPage(team)

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useRevenueSpendByTeamList(setup, send)

	const [viewRevenueCpaDetailsInfo, setViewRevenueCpaDetailsInfo] = useState<TRevenueSpendByTeam | TRevenueSpendByTeamRevenueCpa | null>(null)
	const [viewRevenueSpendDetailsInfo, setViewRevenueSpendDetailsInfo] = useState<TRevenueSpendByTeam | TRevenueSpendByTeamRevenueSpend | null>(null)
	const [viewOfferSpendDetailsInfo, setViewOfferSpendDetailsInfo] = useState<TRevenueSpendByTeam | TRevenueSpendByTeamOfferSpend | null>(null)
	const [viewRevenueDetailsInfo, setViewRevenueDetailsInfo] = useState<TRevenueSpendByTeam | TRevenueSpendByTeamRevenue | null>(null)
	const [viewSpendDetailsInfo, setViewSpendDetailsInfo] = useState<TRevenueSpendByTeam | TRevenueSpendByTeamSpend | null>(null)
	const [showEmptySpendAgencyColumns, setShowEmptySpendAgencyColumns] = useState<boolean>(false)

	const onViewRevenueCpaDetailsInfo = (item: TRevenueSpendByTeam | TRevenueSpendByTeamRevenueCpa, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueCpaDetailsInfo(item)
	}

	const onViewRevenueSpendDetailsInfo = (item: TRevenueSpendByTeam | TRevenueSpendByTeamRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueSpendDetailsInfo(item)
	}

	const onViewOfferSpendDetailsInfo = (item: TRevenueSpendByTeam | TRevenueSpendByTeamOfferSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewOfferSpendDetailsInfo(item)
	}

	const onViewRevenueDetailsInfo = (item: TRevenueSpendByTeam | TRevenueSpendByTeamRevenue, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueDetailsInfo(item)
	}

	const onViewSpendDetailsInfo = (item: TRevenueSpendByTeam | TRevenueSpendByTeamSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewSpendDetailsInfo(item)
	}

	const onShowEmptySpendAgencyColumns = (e: React.MouseEvent) => {
		e.preventDefault()
		setShowEmptySpendAgencyColumns(prevState => !prevState)
	}

	return (
		<>
			<PageTitle title={pageTitleFormat}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						<button
							type="button"
							className="btn btn-primary d-flex align-items-center"
							onClick={onShowEmptySpendAgencyColumns}
						>
							{!showEmptySpendAgencyColumns ? <IconView/> : <IconViewOff/>}
							<span
								className="d-none d-lg-block">{
								!showEmptySpendAgencyColumns ?
									`${t(ALL_LANG_KEY.revenue_spend_by_team_page.btn_show_empty_spend_agency_columns)}` :
									`${t(ALL_LANG_KEY.revenue_spend_by_team_page.btn_hide_empty_spend_agency_columns)}`
							}</span>
						</button>
					</div>

					<Filters
						config={revenueSpendByTeamFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RolePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<RevenueSpendByTeamListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onViewRevenueCpaDetails={onViewRevenueCpaDetailsInfo}
						onViewRevenueSpendDetails={onViewRevenueSpendDetailsInfo}
						onViewOfferSpendDetails={onViewOfferSpendDetailsInfo}
						onViewRevenueDetails={onViewRevenueDetailsInfo}
						onViewSpendDetails={onViewSpendDetailsInfo}
						showEmptySpendAgencyColumns={showEmptySpendAgencyColumns}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!viewRevenueCpaDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByTeamList) &&
				<RevenueSpendByTeamRevenueDetailsModal
					offerType={EOfferType.cpa}
					revenueSpend={viewRevenueCpaDetailsInfo}
					show={!!viewRevenueCpaDetailsInfo}
					take={take}
					onClose={() => {
						setViewRevenueCpaDetailsInfo(null)
						// navigate(href(0).tail)
						//TODO: activate data updates after BE is optimized
						//warmUp()
					}}
				/>
			}

			{
				!!viewRevenueSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByTeamList) &&
				<RevenueSpendByTeamRevenueDetailsModal
					offerType={EOfferType.spend}
					revenueSpend={viewRevenueSpendDetailsInfo}
					show={!!viewRevenueSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewRevenueSpendDetailsInfo(null)
						// navigate(href(0).tail)
						//TODO: activate data updates after BE is optimized
						//warmUp()
					}}
				/>
			}

			{
				!!viewOfferSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByTeamList) &&
				<RevenueSpendByTeamOfferSpendDetailsModal
					revenueSpend={viewOfferSpendDetailsInfo}
					show={!!viewOfferSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewOfferSpendDetailsInfo(null)
						// navigate(href(0).tail)
						//TODO: activate data updates after BE is optimized
						//warmUp()
					}}
				/>
			}

			{
				!!viewRevenueDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByTeamList) &&
				<RevenueSpendByTeamRevenueDetailsModal
					revenueSpend={viewRevenueDetailsInfo}
					show={!!viewRevenueDetailsInfo}
					take={take}
					onClose={() => {
						setViewRevenueDetailsInfo(null)
						// navigate(href(0).tail)
						//TODO: activate data updates after BE is optimized
						//warmUp()
					}}
				/>
			}

			{
				!!viewSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByTeamList) &&
				<RevenueSpendByTeamSpendDetailsModal
					revenueSpend={viewSpendDetailsInfo}
					show={!!viewSpendDetailsInfo}
					take={take}
					onClose={() => {
						setViewSpendDetailsInfo(null)
						// navigate(href(0).tail)
						//TODO: activate data updates after BE is optimized
						//warmUp()
					}}
				/>
			}
		</>
	)
}
